/**
 * Per-person preferences: the account Quick Add starts from and the favourites that get
 * shown first (`updateUserPreferences` in plan step 1.1).
 *
 * One row per profile, guarded by RLS to that profile alone — these are personal choices,
 * not household data, so a spouse's favourites are none of our business.
 */
import { supabase } from '../../../shared/api/supabase/client.js';
import { appErrorException } from '../../../shared/lib/errors.js';

export type Preferences = Readonly<{
  defaultAccountId: string | null;
  favoriteCategoryIds: readonly string[];
  favoriteIncomeSourceIds: readonly string[];
}>;

const EMPTY: Preferences = {
  defaultAccountId: null,
  favoriteCategoryIds: [],
  favoriteIncomeSourceIds: [],
};

export async function fetchPreferences(): Promise<Preferences> {
  const { data, error } = await supabase
    .from('user_preferences')
    .select('default_account_id, favorite_category_ids, favorite_income_source_ids')
    .maybeSingle();
  if (error) throw appErrorException(error);

  // `app.create_household` seeds the row, but a household created before this screen
  // existed, or a member added by email, may not have one yet.
  if (!data) return EMPTY;

  return {
    defaultAccountId: data.default_account_id,
    favoriteCategoryIds: data.favorite_category_ids ?? [],
    favoriteIncomeSourceIds: data.favorite_income_source_ids ?? [],
  };
}

export type PreferencesPatch = Readonly<{
  defaultAccountId?: string | null;
  favoriteCategoryIds?: readonly string[];
  favoriteIncomeSourceIds?: readonly string[];
}>;

/** Upsert so the row is created on first use instead of failing (see `fetchPreferences`). */
export async function updatePreferences(patch: PreferencesPatch): Promise<void> {
  const { data: userData } = await supabase.auth.getUser();
  const user = userData.user;
  if (!user) throw appErrorException(new Error('unauthorized'));

  const { error } = await supabase.from('user_preferences').upsert(
    {
      profile_id: user.id,
      ...(patch.defaultAccountId === undefined
        ? {}
        : { default_account_id: patch.defaultAccountId }),
      ...(patch.favoriteCategoryIds === undefined
        ? {}
        : { favorite_category_ids: [...patch.favoriteCategoryIds] }),
      ...(patch.favoriteIncomeSourceIds === undefined
        ? {}
        : { favorite_income_source_ids: [...patch.favoriteIncomeSourceIds] }),
    },
    { onConflict: 'profile_id' },
  );
  if (error) throw appErrorException(error);
}

export function setDefaultAccount(accountId: string | null): Promise<void> {
  return updatePreferences({ defaultAccountId: accountId });
}

/**
 * Adds or removes one id. The caller passes the list it already has, so the write is a
 * whole-array replacement — an array column has no per-element update, and these lists are
 * a handful of ids long.
 */
export function toggleFavorite(current: readonly string[], id: string): readonly string[] {
  return current.includes(id) ? current.filter((item) => item !== id) : [...current, id];
}
